/**
 * The teaching layer of Number Lab: which numbers divide, and the child-sized
 * reason why. Kept pure so it can be tested without a canvas.
 */

export function divisorsUpTo(n: number, max: number): number[] {
  const out: number[] = [];
  for (let d = 1; d <= max; d++) if (n % d === 0) out.push(d);
  return out;
}

export function digitSum(n: number): number {
  return String(Math.abs(n))
    .split('')
    .reduce((sum, c) => sum + Number(c), 0);
}

/**
 * A one-line hint for "does `rows` divide `total`?" — the classic school
 * divisibility rules, phrased for a 7-year-old, plus the leftover when it fails.
 */
export function divisibilityHint(total: number, rows: number): string {
  if (rows > total) return `You need more blocks to make ${rows} rows`;
  const rest = total % rows;
  const fits = rest === 0;
  const last = total % 10;

  if (rows === 1) return fits ? '1 fits into every number' : '';

  if (fits) {
    switch (rows) {
      case 2:
        return `${total} ends in ${last}, so it is even — 2 fits`;
      case 3:
        return `${digitsPhrase(total)} = ${digitSum(total)}, and 3 fits into that — so 3 fits`;
      case 4:
        return `Half of ${total} is ${total / 2}, which is even — so 4 fits`;
      case 5:
        return `${total} ends in ${last}, so 5 fits`;
      case 6:
        return `${total} is even and 3 fits — so 6 fits`;
      case 9:
        return `${digitsPhrase(total)} = ${digitSum(total)}, and 9 fits into that — so 9 fits`;
      case 10:
        return `${total} ends in 0, so 10 fits`;
      default:
        return `${rows} fits into ${total} exactly ${total / rows} times`;
    }
  }

  switch (rows) {
    case 2:
      return `${total} ends in ${last}, so it is odd — 1 is left over`;
    case 3:
      return `${digitsPhrase(total)} = ${digitSum(total)}, and 3 does not fit into that — ${rest} left over`;
    case 5:
      return `${total} does not end in 0 or 5 — ${rest} left over`;
    case 9:
      return `${digitsPhrase(total)} = ${digitSum(total)}, and 9 does not fit into that — ${rest} left over`;
    case 10:
      return `${total} does not end in 0 — ${rest} left over`;
    default:
      return `${rows} rows leaves ${rest} over — try another number`;
  }
}

function digitsPhrase(n: number): string {
  return String(n).split('').join(' + ');
}
